import { PresetImageDTO } from 'api/api_code';
import { SHIELD } from 'components/UI/Icon/icons/illustration.constants';
import { SPORT } from 'constants/sports';
import { KeyboardEvent } from 'react';

export const get = (obj, path, defValue) => {
	// If path is not defined or it has false value
	if (!path) return undefined;
	// Check if path is string or array. Regex : ensure that we do not have '.' and brackets.
	// Regex explained: https://regexr.com/58j0k
	const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g);
	// Find value if exist return otherwise return undefined value;
	return (
		pathArray.reduce((prevObj, key) => prevObj && prevObj[key], obj) ?? defValue
	);
};

const preventDragOffCanvas = (obj) => {
	const objBoundingRect = obj.getBoundingRect(true);

	// Y coordinates
	if (objBoundingRect.top < 0) {
		// Object top exceeding canvas from the top
		obj.top = obj.top - objBoundingRect.top;
	} else if (objBoundingRect.top + objBoundingRect.height > obj.canvas.height) {
		// Object bottom exceeding canvas from the bottom
		obj.top =
			obj.canvas.height -
			objBoundingRect.height +
			(obj.top - objBoundingRect.top);
	}

	// X coordinates
	if (objBoundingRect.left < 0) {
		// Object left exceeding canvas from the left
		obj.left = obj.left - objBoundingRect.left;
	} else if (objBoundingRect.left + objBoundingRect.width > obj.canvas.width) {
		// Object right exceeding canvas from the right
		obj.left =
			obj.canvas.width -
			objBoundingRect.width +
			(obj.left - objBoundingRect.left);
	}
};

const preventScalingOffCanvas = (obj) => {
	const br = obj.getBoundingRect(true);
	const canvas = obj.canvas;

	if (br.top + br.height > canvas.height) {
		const maxHeight = canvas.height - br.top;
		obj.scaleY = obj.scaleY * (maxHeight / br.height);
	}
	if (br.left + br.width > canvas.width) {
		const maxWidth = canvas.width - br.left;
		obj.scaleX = obj.scaleX * (maxWidth / br.width);
	}
};

export const checkPositionLimits = (obj) => {
	// Clamp position only — never resize on drag
	preventDragOffCanvas(obj);
	obj.setCoords();
};

export const checkLimits = (obj) => {
	// Check positioning
	preventDragOffCanvas(obj);
	// Update coords before scaling check so getBoundingRect returns fresh values
	obj.setCoords();
	// Check scaling
	preventScalingOffCanvas(obj);
};

/**
 * Returns the four points of the rectangle normalized
 * @param e event from canvas object
 * @param {number} width Width of canvas
 * @param {number} height Height of canvas
 */
export const getObjectCanvasValues = (e, width: number, height: number) => {
	const obj = e.target;
	return [
		{ x: obj.left / width, y: obj.top / height },
		{ x: (obj.left + obj.width * obj.scaleX) / width, y: obj.top / height },
		{
			x: (obj.left + obj.width * obj.scaleX) / width,
			y: (obj.top + obj.height * obj.scaleY) / height,
		},
		{ x: obj.left / width, y: (obj.top + obj.height * obj.scaleY) / height },
	];
};

export const normalizePoint = (
	x: number,
	y: number,
	width: number,
	height: number
) => {
	return {
		x: x / width,
		y: y / height,
	};
};

export const deNormalizePoint = (
	x: number,
	y: number,
	width: number,
	height: number
) => {
	return {
		x: x * width,
		y: y * height,
	};
};

export const getObjectCanvasCalculatedValues = (obj) => {
	return {
		image_uuid: obj.id,
		position: {
			x: obj.left,
			y: obj.top,
			width: obj.width * obj.scaleX,
			height: obj.height * obj.scaleY,
		},
		image: {
			base64: obj.base64,
			width: obj.width * obj.scaleX,
			height: obj.height * obj.scaleY,
		},
		name: `name-${obj.id}`,
	} as PresetImageDTO;
};

export const getBase64 = (
	file
): Promise<{
	base64: string;
	format: string;
	width: number;
	height: number;
}> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onerror = () =>
			reject(new Error(`FileReader failed to read file: ${file.name}`));
		reader.onload = () => {
			const dataUrl = reader.result as string;
			const img = new Image();
			img.onerror = () =>
				reject(new Error(`Failed to decode image: ${file.name}`));
			img.onload = function () {
				const mimeMatch = dataUrl.match(/^data:([^;]+);/);
				const fullMime = mimeMatch ? mimeMatch[1] : file.type || 'image/png';
				const format = fullMime.includes('/')
					? fullMime.split('/')[1]
					: fullMime;
				resolve({
					base64: dataUrl.replace('data:', '').replace(/^.+,/, ''),
					format,
					width: img.width,
					height: img.height,
				});
			};
			img.src = dataUrl;
		};
	});
};

export const getFabricBorderStyle = () => {
	return {
		hasBorders: true,
		borderColor: 'white',
		borderDashArray: [5, 4],
		lockRotation: true,
		transparentCorners: false,
		cornerColor: '#FA9900',
		cornerSize: 8,
		borderOpacityWhenMoving: 1,
	};
};

export const getSportIllus = (key) => {
	if (key in SPORT) return SPORT[key].illustration;
	return SHIELD;
};

export const getBackgroundImage = (uuid) => {
	if (uuid in SPORT) return SPORT[uuid].background;
	return 'summaryBackground.jpg';
};

export const getSportName = (uuid) => {
	if (uuid in SPORT) return SPORT[uuid].name;
	return 'Sport';
};

export const getSportId = (uuid) => {
	if (uuid in SPORT) return SPORT[uuid].id;
};

export const snakeCaseToCamelCase = (str: string) =>
	str
		.split('_')
		.filter((x) => x.length > 0)
		.map((x, i) =>
			i === 0
				? x.toLowerCase()
				: x.charAt(0).toUpperCase() + x.slice(1).toLowerCase()
		)
		.join('');

export const getRegexPattern = (searchString: string) => {
	const symbols = /[-\[\]{}()*+?.,\\^$|#]/g;
	const hasSymbols = symbols.test(searchString);

	if (hasSymbols) {
		const searchStringWithSymbols = searchString.replace(symbols, '\\$&');
		return new RegExp(`${searchStringWithSymbols}\ \\((\\d)\\)`, 's');
	}

	return new RegExp(`${searchString}\ \\((\\d)\\)`, 's');
};
export const validateOnlyNumbers = (e: KeyboardEvent<HTMLInputElement>) => {
	if (!/\d/.test(e.key) && e.key !== 'Backspace') {
		e.preventDefault();
	}
};
